/**
 * EtudiantLayout — wrapper des pages /etudiant/*
 * Délègue toute la chrome à <AuthLayoutShell>.
 *
 * V10 : badge entretiens (invitations pending) + badge immersions (demandes company pending).
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { AuthLayoutShell } from './AuthLayoutShell';
import { SidebarLink } from '@/components/passage/SidebarLink';

/** Badge affiché uniquement quand count > 0. Silencieux en cas d'erreur réseau. */
function PendingBadge({ count, label }: { count: number; label: string }) {
  if (count === 0) return null;
  return (
    <span
      aria-label={label}
      className="ml-auto inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-passage-tomate px-1 text-[10px] font-bold text-white"
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

function InterviewPendingBadge() {
  const { data = 0 } = useQuery({
    queryKey: ['etudiant', 'entretiens-pending-count'],
    queryFn: () =>
      apiFetch<{ items: Array<{ status: string }> }>('/etudiant/entretiens')
        .then((r) => r.items.filter((i) => i.status === 'pending').length)
        .catch(() => 0),
    staleTime: 60_000,
  });
  return (
    <PendingBadge
      count={data}
      label={`${data} entretien${data > 1 ? 's' : ''} en attente`}
    />
  );
}

/**
 * Badge candidatures : nombre total de messages non-lus sur toutes les candidatures.
 * Partage le cache ['etudiant', 'applications'] avec CandidaturesPage.
 * L'API retourne déjà unreadCount calculé via message_read_receipts.
 */
function CandidatureMessagesBadge() {
  const { data = 0 } = useQuery({
    queryKey: ['etudiant', 'applications'],
    queryFn: () =>
      apiFetch<{ applications: Array<{ unreadCount: number }> }>(
        '/etudiant/applications',
      ).catch(() => ({ applications: [] as Array<{ unreadCount: number }> })),
    select: (r) => r.applications.reduce((sum, a) => sum + (a.unreadCount ?? 0), 0),
    staleTime: 60_000,
  });
  return (
    <PendingBadge
      count={data}
      label={`${data} message${data > 1 ? 's' : ''} non lu${data > 1 ? 's' : ''}`}
    />
  );
}

/**
 * Badge immersions : demandes envoyées par une entreprise auxquelles
 * le candidat n'a pas encore répondu (status='requested', initiator='company').
 * Partage le cache ['etudiant', 'immersions'] avec ImmersionsPage via select.
 */
function ImmersionPendingBadge() {
  // queryKey 'candidate' correspond au viewer='candidate' de ImmersionsList
  const { data = 0 } = useQuery({
    queryKey: ['candidate', 'immersions'],
    queryFn: () =>
      apiFetch<{ items: Array<{ status: string; initiator: string }> }>(
        '/etudiant/immersions',
      ).catch(() => ({ items: [] as Array<{ status: string; initiator: string }> })),
    select: (r) =>
      r.items.filter(
        (i) => i.status === 'requested' && i.initiator === 'company',
      ).length,
    staleTime: 60_000,
  });
  return (
    <PendingBadge
      count={data}
      label={`${data} immersion${data > 1 ? 's' : ''} en attente`}
    />
  );
}

export function EtudiantLayout() {
  return (
    <AuthLayoutShell
      homePath="/etudiant"
      roleLabel="Candidat"
      roleColor="beurre"
      navAriaLabel="Navigation candidat"
      sidebar={
        <>
          <SidebarLink to="/etudiant" end>Tableau de bord</SidebarLink>
          <SidebarLink to="/etudiant/cv">Mon CV</SidebarLink>
          <SidebarLink to="/etudiant/profil">Mon profil</SidebarLink>
          <SidebarLink to="/etudiant/publication">Publication</SidebarLink>
          <hr className="my-3 border-t-2 border-dashed border-passage-hairline" />
          <SidebarLink to="/etudiant/annonces">Annonces</SidebarLink>
          <SidebarLink to="/etudiant/guide-alternance">Guide alternance</SidebarLink>
          <SidebarLink to="/etudiant/candidatures">
            <span className="flex items-center justify-between gap-1">
              <span>Mes candidatures</span>
              <CandidatureMessagesBadge />
            </span>
          </SidebarLink>
          <SidebarLink to="/etudiant/immersions">
            <span className="flex items-center justify-between gap-1">
              <span>Immersions</span>
              <ImmersionPendingBadge />
            </span>
          </SidebarLink>
          <SidebarLink to="/etudiant/entretiens">
            <span className="flex items-center justify-between gap-1">
              <span>Entretiens vidéo</span>
              <InterviewPendingBadge />
            </span>
          </SidebarLink>
          <SidebarLink to="/etudiant/references">Références</SidebarLink>
          <SidebarLink to="/etudiant/reseau">Mon réseau</SidebarLink>
          <SidebarLink to="/etudiant/qr-code">QR code</SidebarLink>
          <SidebarLink to="/etudiant/aide">Aide</SidebarLink>
          <hr className="my-3 border-t-2 border-dashed border-passage-hairline" />
          <SidebarLink to="/compte/securite">Sécurité</SidebarLink>
          <SidebarLink to="/compte/donnees">Mes données</SidebarLink>
        </>
      }
    />
  );
}
